import { PickerOverlay } from "filestack-react"
import React, { useState } from "react"
import { createPortal } from "react-dom"

import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { AnimatedModal } from "../../../../components/shared/Modal"

const tabs = [
  { id: "uploaded", label: "Uploaded images" },
  { id: "upload", label: "Upload new" },
  { id: "stock", label: "Curated photos" }
]

/**
 * ImagePickerV2 - Reusable image picker component for WebsiteBuilderV2
 * Provides a unified interface for:
 * - Viewing and selecting from previously uploaded images
 * - Uploading new images
 * - Selecting from curated/stock photos
 * - Cropping images with custom aspect ratios
 *
 * @param {Object} props
 * @param {Object} props.practice - Practice object with allUploadedImages and defaultHeroImages
 * @param {Function} props.onImageSelect - Callback when image is selected (receives { handle, provider, imageUrl })
 * @param {boolean} props.fetching - Whether an upload/operation is in progress
 * @param {boolean} props.showCurateTab - Whether to show the curated photos tab (default: true)
 * @param {boolean} props.hideS3Images - Whether to hide S3 images from uploaded list (default: false)
 * @param {number} props.aspectRatio - Aspect ratio for cropping (e.g., 16/9, 4/3, 1, null for free crop)
 * @param {string} props.currentImageUrl - Current image URL to highlight in the picker
 * @param {React.ReactNode} props.trigger - Custom trigger element (defaults to upload button)
 */
export default function ImagePickerV2({
  practice,
  onImageSelect,
  fetching,
  showCurateTab = true,
  hideS3Images = false,
  aspectRatio = null,
  currentImageUrl = null,
  trigger = null
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const initialTab = practice?.allUploadedImages?.length === 0 ? "upload" : "uploaded"
  const [activeTab, setActiveTab] = useState(initialTab)
  const [cropModalOpen, setCropModalOpen] = useState(false)

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl === selectedImage ? null : imageUrl)
  }

  const handleSave = () => {
    if (!selectedImage) return

    setIsOpen(false)
    const handle = selectedImage.split("/").pop()
    const provider = selectedImage.includes("amazonaws.com") ? "amazon_s3" : "filestack"

    onImageSelect({
      handle,
      provider,
      imageUrl: selectedImage
    })
    setSelectedImage(null)
  }

  const handleUploadDone = (res) => {
    const file = res.filesUploaded[0]
    const handle = file.handle
    const provider = "filestack"

    setIsOpen(false)
    onImageSelect({
      handle,
      provider,
      imageUrl: file.url
    })
  }

  const filteredTabs = showCurateTab ? tabs : tabs.filter((tab) => tab.id !== "stock")
  const uploadedImages =
    practice?.allUploadedImages?.filter((imageUrl) => !hideS3Images || !imageUrl.includes("amazonaws.com")) || []

  const renderTabContent = () => {
    switch (activeTab) {
      case "upload":
        return (
          <div className="p-4">
            <div className="relative mb-4 shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
              <p className="block leading-[20px]">Click below to upload a new image from your device.</p>
            </div>
            <FilePicker
              as="button"
              onUploadDone={handleUploadDone}
              disabled={fetching}
              transformations={aspectRatio ? { crop: { aspectRatio, force: true } } : { crop: { force: true } }}
              className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 rounded border border-[#0d9da4] bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0d9da4] transition-colors hover:bg-[#e2f3f4]"
              maxFiles={1}>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload
            </FilePicker>
          </div>
        )
      case "uploaded":
        return (
          <div className="h-[calc(100vh-350px)] overflow-y-auto p-4">
            {uploadedImages.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {uploadedImages.map((imageUrl, index) => {
                  const thumbUrl = imageUrl.replace("amazonaws.com", "amazonaws.com/thumbnails")
                  const isSelected = selectedImage === imageUrl
                  const isCurrent = currentImageUrl === imageUrl

                  return (
                    <div
                      key={index}
                      className={`relative cursor-pointer rounded-lg transition-all duration-200 ${
                        isSelected ? "ring-4 ring-[#0d9da4]" : ""
                      } ${isCurrent && !isSelected ? "ring-2 ring-[#55778a]" : ""}`}
                      onClick={() => handleImageSelect(imageUrl)}>
                      <img
                        src={thumbUrl}
                        alt={`Uploaded image ${index + 1}`}
                        className="aspect-video w-full rounded-lg bg-gray-100 object-cover"
                      />
                      {isCurrent && !isSelected && (
                        <div className="absolute right-2 top-2 rounded bg-[#0d9da4] px-2 py-1 text-xs font-bold text-white">
                          Current
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-[#0d9da4] bg-opacity-[0.08]">
                          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-lg">
                            <svg className="h-8 w-8 text-[#0d9da4]" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="mt-2 text-[14px] text-[#55778a]">No images uploaded yet.</p>
                  <p className="mt-1 text-[13px] text-[#55778a]">Upload images to see them here.</p>
                </div>
              </div>
            )}
          </div>
        )
      case "stock":
        return (
          <div className="h-[calc(100vh-350px)] overflow-y-auto p-4">
            {practice?.defaultHeroImages?.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {practice.defaultHeroImages.map((imageUrl, index) => {
                  const thumbUrl = imageUrl.replace("amazonaws.com", "amazonaws.com/thumbnails")
                  const isSelected = selectedImage === imageUrl

                  return (
                    <div
                      key={index}
                      className={`cursor-pointer rounded-lg transition-all duration-200 ${
                        isSelected ? "ring-4 ring-[#0d9da4]" : ""
                      }`}
                      onClick={() => handleImageSelect(imageUrl)}>
                      <img
                        src={thumbUrl}
                        alt={`Curated image ${index + 1}`}
                        className="aspect-video w-full rounded-lg bg-gray-100 object-cover"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-[#0d9da4] bg-opacity-[0.08]">
                          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-lg">
                            <svg className="h-8 w-8 text-[#0d9da4]" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-[14px] text-[#55778a]">No curated photos available.</p>
                </div>
              </div>
            )}
          </div>
        )
      default:
        return null
    }
  }

  function CropModal() {
    return createPortal(
      <PickerOverlay
        apikey={process.env.FILESTACK_API_KEY}
        onUploadDone={(res) => {
          handleUploadDone(res)
          setCropModalOpen(false)
        }}
        pickerOptions={{
          transformations: aspectRatio ? { crop: { aspectRatio, force: true } } : { crop: { force: true } },
          fromSources: ["local_file_system"],
          onClose: () => setCropModalOpen(false)
        }}
      />,
      document.body
    )
  }

  // Default trigger button
  const defaultTrigger = (
    <button
      onClick={() => setIsOpen(true)}
      disabled={fetching}
      className="relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 rounded border border-[#0d9da4] bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0d9da4] transition-colors hover:bg-[#e2f3f4] disabled:cursor-not-allowed disabled:opacity-50">
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      {initialTab === "uploaded" ? "Change Image" : "Upload Image"}
    </button>
  )

  return (
    <>
      {trigger ? <div onClick={() => setIsOpen(true)}>{trigger}</div> : defaultTrigger}

      {/* Image upload modal */}
      <AnimatedModal
        visible={isOpen}
        hideModal={() => {
          setIsOpen(false)
          setSelectedImage(null)
        }}
        showFooter={true}
        actionButtonCopy={activeTab === "upload" ? "Upload" : selectedImage ? "Use Image" : "Select Image"}
        footerButtons={
          activeTab === "uploaded" && selectedImage
            ? [
                {
                  label: "Crop",
                  type: "secondary",
                  onClick: () => setCropModalOpen(true),
                  show: true
                }
              ]
            : []
        }
        saveDisabled={activeTab !== "upload" && !selectedImage}
        onSave={activeTab === "upload" ? () => {} : handleSave}
        dialogClassName="max-h-[100vh] flex flex-col w-[720px] md:w-full">
        <div className="flex h-full flex-col">
          <div className="relative mb-4 w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[20px] font-bold text-[#0b3954]">
            <p className="block leading-[28px]">Image Selection</p>
          </div>

          {/* Tabs */}
          <div className="mb-4 flex gap-6 border-b border-[#e4eff6]">
            {filteredTabs.map((tab) => (
              <button
                key={tab.id}
                className={`relative pb-3 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold transition-colors ${
                  tab.id === activeTab
                    ? "border-b-2 border-[#0d9da4] text-[#0d9da4]"
                    : "text-[#55778a] hover:text-[#0b3954]"
                }`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grow overflow-hidden">{renderTabContent()}</div>
        </div>
        {/* Crop modal */}
        {cropModalOpen && <CropModal />}
      </AnimatedModal>
    </>
  )
}
